import { FieldError, Merge, FieldErrorsImpl } from "react-hook-form";

export type CustomHookFormError = FieldError | Merge<FieldError, FieldErrorsImpl<any>>;

type CustomInputType = "text" | "email" | "url";

interface CustomFormFieldValidation {
  type?: CustomInputType | ((watch: any) => CustomInputType);
  /** Conditionally require with Hook Form watch()
   * ```
   * { isRequired: (watch) => watch.redirectDestinationSelect === "custom-url" }
   * ```  */
  isRequired?: boolean | ((watch: any) => boolean);
  isValidUrl?: boolean;
  isRequiredValidationMessage?: string;
  isValidUrlValidationMessage?: string;
  minLength?: number;
  maxLength?: number;
  maxLengthValidationMessage?: string;
  pattern?: RegExp;
  includes?: string;
  startsWith?: string;
  endsWith?: string;
  customValidationMessage?: string;
  /** Return true if valid, or an error message string if invalid. */
  validate?: (value: unknown) => true | string;
  /** Custom renderer for the error message (e.g. append a link). */
  renderError?: (message: string) => React.ReactNode;
}

export interface CustomBaseFormFieldProps {
  component: "input" | "select" | "textarea" | "hidden";
  name: string;
  label: string;
  eyebrow?: string;
  helperText?: string;
  placeholder?: string;
  validations?: CustomFormFieldValidation;
  defaultValue?: string | boolean;
  error?: CustomHookFormError | React.ReactNode;
  /**
   * Exclude this field from being saved in the form data. Set to true for any field that causes a useful effect but isn't meant to be saved. */
  exclude?: boolean;
  /**
   * Conditionally show field based on the value of another field. Example use:
   * ```
   * {
      name: "redirectDestination",
      condition: (watch) => watch.redirectDestinationSelect === "custom-url",
      [other props...]
    },
   * ``` */
  condition?: (watch: any) => boolean;
}

type CustomFormFieldProps<T> = Omit<React.HTMLProps<T>, "name" | "label"> &
  CustomBaseFormFieldProps;

interface CustomHiddenFieldProps extends Pick<CustomBaseFormFieldProps, "name" | "defaultValue"> {
  component: "hidden";
}

export interface CustomSelectProps extends CustomFormFieldProps<HTMLSelectElement> {
  component: "select";
  options: {
    name: string;
    value: string;
  }[];
}

export interface CustomTextareaProps extends CustomFormFieldProps<HTMLTextAreaElement> {
  component: "textarea";
}

export interface CustomInputProps extends CustomFormFieldProps<HTMLInputElement> {
  component: "input";
  addonStart?: React.ReactNode;
  addonEnd?: React.ReactNode;
}

export type CustomFormField =
  | CustomHiddenFieldProps
  | CustomSelectProps
  | CustomTextareaProps
  | CustomInputProps;

/**
 * Prepare form values for API submission
 * - Recursively set `""` and `null` values to `undefined`
 * - Make sure stringified booleans are real booleans */
export const normalizeValues = (obj: Record<string, any>): Record<string, any> => {
  const isObject = (obj: any) => typeof obj === "object" && obj !== null;

  const isBlankString = (value: any) => typeof value === "string" && value.trim() === "";

  const isBooleanAsString = (value: any) =>
    typeof value === "string" && (value === "true" || value === "false");

  for (const key in obj) {
    if (isObject(obj[key])) {
      obj[key] = normalizeValues(obj[key]); // Recurse into nested objects
    }
    if (isBlankString(obj[key])) {
      obj[key] = undefined; // Convert empty strings to undefined
    }
    if (isBooleanAsString(obj[key])) {
      obj[key] = JSON.parse(obj[key]); // Convert string booleans to boolean
    }
  }
  return obj;
};
